import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const memberId = params.get('memberId');

  const [loading, setLoading] = useState(!!token && !!memberId);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !memberId) return;

    const unsubscribe = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/member/unsubscribe?token=${encodeURIComponent(token)}&memberId=${encodeURIComponent(memberId)}`,
        );
        if (res.ok) {
          setSuccess(true);
        } else {
          const data = await res.json().catch(() => ({ error: 'Unsubscribe failed' }));
          setError(data.error ?? 'Unsubscribe failed');
        }
      } catch {
        setError('Unable to reach the server. Please try again later.');
      }
      setLoading(false);
    };

    void unsubscribe();
  }, [token, memberId]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <Sparkles className="w-6 h-6 text-[#5B4FE8]" />
        <span className="text-xl font-bold text-[#111827]">
          Swipe <span className="text-[#5B4FE8]">Opinion</span>
        </span>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center space-y-4">
        {loading && (
          <>
            <Loader2 className="w-12 h-12 text-[#5B4FE8] mx-auto animate-spin" />
            <h1 className="text-2xl font-bold text-[#111827]">Unsubscribing...</h1>
            <p className="text-[#6B7280]">Processing your request.</p>
          </>
        )}

        {!loading && success && (
          <>
            <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto" />
            <h1 className="text-2xl font-bold text-[#111827]">You've been unsubscribed</h1>
            <p className="text-[#6B7280]">
              You won't receive survey invitation emails anymore. You can re-subscribe anytime from your profile settings.
            </p>
          </>
        )}

        {!loading && error && (
          <>
            <AlertCircle className="w-16 h-16 text-[#EF4444] mx-auto" />
            <h1 className="text-2xl font-bold text-[#111827]">Something went wrong</h1>
            <p className="text-[#6B7280]">{error}</p>
          </>
        )}

        {!loading && !token && !memberId && !success && !error && (
          <>
            <AlertCircle className="w-16 h-16 text-[#F97316] mx-auto" />
            <h1 className="text-2xl font-bold text-[#111827]">Invalid link</h1>
            <p className="text-[#6B7280]">This unsubscribe link is missing required parameters.</p>
          </>
        )}

        {!loading && (
          <div className="flex flex-col gap-2 pt-2">
            <Link to="/profile" className="text-[#5B4FE8] font-medium hover:underline">
              Manage email preferences
            </Link>
            <Link to="/surveys" className="text-[#6B7280] text-sm hover:underline">
              Browse surveys
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
